import CloseIcon from '@mui/icons-material/Close';
import PopOutIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Drawer, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';

import { AttributesPanel } from './AttributesPanel';
import { CallHistoryPanel } from './CallHistoryPanel';
import { DialPanel } from './DialPanel';
import { DirectoryPanel } from './DirectoryPanel';
import { QueuesPanel } from './QueuesPanel';
import { AgentStatusPill } from '../../molecules/AgentStatusPill';
import { useConnectOptional } from '../ConnectProvider';

// Slide-out drawer anchored to the left edge, hosting the softphone tabs.
// Mounted once at the MainLayout level so it survives route changes and
// stays open as the user navigates.

type TabKey = 'dial' | 'attributes' | 'history' | 'directory' | 'queues';

const PANEL_WIDTH = 360;

export const ConnectSecondaryDrawer: React.FC = () => {
  const ctx = useConnectOptional();
  const [tab, setTab] = useState<TabKey>('dial');
  if (!ctx) return null;

  const { agent, contact, initState, isPanelOpen, setPanelOpen, popOut } = ctx;
  const close = () => setPanelOpen(false);
  const isDuplicate = initState.status === 'duplicateTab';
  const isError = initState.status === 'error';

  return (
    <Drawer
      open={isPanelOpen}
      onClose={close}
      anchor="left"
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: PANEL_WIDTH,
          bgcolor: 'background.default',
          borderRight: '1px solid',
          borderColor: 'v4.border',
          boxShadow: '0 0 24px rgba(15,23,42,0.18)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: 'v4.sidebar',
          color: 'common.white',
          borderBottom: '1px solid',
          borderColor: '#334155',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <AgentStatusPill agent={agent} contact={contact} />
        </Box>
        <Tooltip title="Open in new window">
          <IconButton
            onClick={popOut}
            size="small"
            aria-label="Open Amazon Connect in a new window"
            sx={{ color: '#cbd5e1', '&:hover': { color: '#fff' } }}
          >
            <PopOutIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close phone panel">
          <IconButton
            onClick={close}
            size="small"
            aria-label="Close phone panel"
            sx={{ color: '#cbd5e1', '&:hover': { color: '#fff' } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => setTab(v)}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          borderBottom: '1px solid',
          borderColor: 'v4.border',
          bgcolor: 'v4.surface',
          '& .MuiTab-root': { minHeight: 40, fontSize: 12, textTransform: 'none' },
        }}
      >
        <Tab value="dial" label="Dial" />
        <Tab value="attributes" label="Info" />
        <Tab value="history" label="History" />
        <Tab value="directory" label="Directory" />
        <Tab value="queues" label="Queues" />
      </Tabs>

      <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'v4.surface' }}>
        {isDuplicate ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Phone is active in another tab
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Amazon Connect only supports one active session per browser. Close the other tab to
                use the softphone here.
              </Typography>
            </Alert>
          </Box>
        ) : isError ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Phone failed to start
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {initState.errorMessage ?? 'Unknown error'}
              </Typography>
            </Alert>
          </Box>
        ) : (
          <>
            {tab === 'dial' && <DialPanel />}
            {tab === 'attributes' && <AttributesPanel />}
            {tab === 'history' && <CallHistoryPanel />}
            {tab === 'directory' && <DirectoryPanel />}
            {tab === 'queues' && <QueuesPanel />}
          </>
        )}
      </Box>
    </Drawer>
  );
};
